/**
 * Move sheet — the touch replacement for drag & drop (INVENTORY §2 row 2,
 * §4.11; prototype lines 892-920).
 *
 * Opened by a capsule TAP on either board; the board screens own the 450 ms
 * post-drop suppression that stops a drop from also opening this
 * (`lib/drag.ts#tapSuppressed`).
 *
 * z-index 85 — the one sheet that sits above the other overlays, because it can
 * be reached from a screen that already has the filters sheet open. Its head is
 * custom (38×57 cover + name + sub + ✕), so `Sheet` gets no `header` prop.
 *
 * Action semantics differ per kind and both are deliberate:
 *   int   `voteSet` — SET, not toggle. It also moves the id to the END of
 *         `intOrder` and toasts (unlike list mode's letter buttons, which are
 *         `vote`). Five acts: the four calls, then `? NO CALL YET`.
 *   goty  `placeTier`, plus `↩ BACK TO TO-DO` (`unplaceTier`) once placed.
 */

import { CoverArt } from '../components/CoverArt.tsx';
import { Sheet } from '../components/Sheet.tsx';
import { useArt } from '../components/useArt.ts';
import { CALLS, LVL } from '../lib/logic.ts';
import type { Tier, VoteInput } from '../lib/types.ts';
import { useQd } from '../store/index.ts';
import { moveSheetVM } from '../store/selectors.ts';

const MONO = 'var(--gt-font-mono)';

const ACT_ROW = {
  height: 48,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '0 14px',
  fontFamily: MONO,
  fontSize: 11,
  letterSpacing: '.03em',
  cursor: 'pointer',
  boxSizing: 'border-box',
} as const;

/**
 * `actSt` (prototype line 2501) — an inactive action is always the neutral
 * outline; the ACTIVE one takes the colour of what it means, which is why
 * "C · PLAY IT WITHOUT ME" and "✕ CUT" go magenta and "? NO CALL YET" stays
 * grey.
 */
const actSt = (on: boolean, clr: string, soft: string) =>
  on
    ? { border: '1px solid ' + clr, color: clr, background: soft }
    : { border: '1px solid var(--gt-border-2)', color: 'var(--gt-text-3)', background: 'transparent' };

/**
 * Tone per act key, for BOTH sheet kinds — the int acts in lowercase, the GOTY
 * tiers in upper. They cannot collide, and the shared map is why the GOTY rows
 * below must survive an edit made for interests.
 *
 * The four calls read their pair off `LVL`, which is also where `moveSheetVM`
 * gets the act copy: derived, so a fifth call could never ship a toneless act.
 * That matters because the lookup falls back to accent green rather than
 * failing — a missing key paints a wrong-coloured act and reports nothing.
 *
 * CLEAR is keyed `w`, not `c`. On the binary board `c` WAS clear; under four
 * tiers it is PLAY IT WITHOUT ME, so the two would have quietly swapped tones.
 * `w` is the Interests board's own id for the same row (`intRowVote`).
 */
const ACT_TONE: Record<string, [string, string]> = {
  ...(Object.fromEntries(CALLS.map((l) => [l, [LVL[l].c, LVL[l].lab]])) as Record<string, [string, string]>),
  w: ['var(--gt-text-5)', 'var(--gt-raised)'],
  S: ['var(--acc)', 'var(--accSoft)'],
  A: ['var(--acc)', 'var(--accSoft)'],
  B: ['var(--acc)', 'var(--accSoft)'],
  X: ['var(--acc2)', 'var(--acc2Soft)'],
};

const NUDGE = {
  flex: 1,
  height: 44,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  border: '1px solid var(--gt-border-2)',
  color: 'var(--gt-text-3)',
  fontFamily: MONO,
  fontSize: '10.5px',
  cursor: 'pointer',
  boxSizing: 'border-box',
} as const;

export default function MoveSheet(): JSX.Element | null {
  const s = useQd();
  const v = moveSheetVM(s);
  // Hooks before the guard: the closed branch of `moveSheetVM` carries no id, so
  // the probe key collapses to `'p'` with an empty ladder and resolves nothing.
  const art = useArt((v.shShow ? v.id : '') + 'p', v.shShow ? v.shCoverCands : []);

  if (!v.shShow) return null;

  const isInt = v.kind === 'int';
  const nudge = (dir: 1 | -1): void => {
    if (isInt) void s.nudgeInt(v.id, dir);
    else void s.nudgeTier(v.id, dir);
  };
  /** `done(fn)` (prototype line 2500) — every action fires, then closes. */
  const done = (fn: () => void) => (): void => {
    fn();
    s.closeSheet();
  };

  return (
    <Sheet label="Move sheet" zIndex={85} onClose={s.closeSheet}>
      {/* custom head — 38×57 cover + name + sub + ✕ */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 11,
          padding: '13px 14px',
          borderBottom: '1px solid var(--gt-line)',
        }}
      >
        <div
          style={{
            position: 'relative',
            width: 38,
            height: 57,
            flex: 'none',
            background: v.shFall,
            overflow: 'hidden',
            border: '1px solid var(--gt-border-2)',
          }}
        >
          <CoverArt style={{ position: 'absolute', inset: 0 }} fs={7} ini={v.shIni} hasCover={art.hasCover} cover={art.cover} />
        </div>
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div
            style={{
              fontWeight: 600,
              fontSize: '13.5px',
              color: 'var(--gt-text)',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {v.shName}
          </div>
          <div style={{ fontFamily: MONO, fontSize: 9, color: 'var(--gt-faint)' }}>{v.shSub}</div>
        </div>
        <div
          onClick={s.closeSheet}
          style={{
            width: 40,
            height: 40,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: MONO,
            fontSize: 15,
            color: 'var(--gt-text-5)',
            cursor: 'pointer',
            flex: 'none',
          }}
        >
          ✕
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '14px 14px 18px' }}>
        {v.shActs.map((a) => {
          const tone = ACT_TONE[a.key] ?? ['var(--acc)', 'var(--accSoft)'];
          return (
            <div
              key={a.key}
              onClick={done(() => {
                if (v.kind === 'int') void s.voteSet(v.id, a.v as VoteInput);
                // `shActs` is built from `TIERS`, so `a.v` is already a `Tier`
                // here; the cast only re-states which arm of the union this is.
                else void s.placeTier(v.id, a.v as Tier);
              })}
              style={{ ...ACT_ROW, ...actSt(a.on, tone[0], tone[1]) }}
            >
              {a.l}
            </div>
          );
        })}

        {v.shCanUnplace ? (
          <div onClick={done(() => void s.unplaceTier(v.id))} style={{ ...ACT_ROW, ...actSt(false, '', '') }}>
            ↩ BACK TO TO-DO
          </div>
        ) : null}

        {v.shHasNudge ? (
          <div style={{ display: 'flex', gap: 8 }}>
            <div onClick={() => nudge(-1)} style={NUDGE}>
              ◂ MOVE EARLIER
            </div>
            <div onClick={() => nudge(1)} style={NUDGE}>
              MOVE LATER ▸
            </div>
          </div>
        ) : null}

        {/* `openGame` already nulls `sheet`, so this needs no explicit close. */}
        <div onClick={() => s.openGame(v.id)} style={{ ...ACT_ROW, ...actSt(false, '', '') }}>
          ▸ OPEN GAME PAGE
        </div>

        <div
          onClick={s.closeSheet}
          style={{
            height: 46,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '1px dashed var(--gt-border-2)',
            color: 'var(--gt-muted)',
            fontFamily: MONO,
            fontSize: 10,
            cursor: 'pointer',
            boxSizing: 'border-box',
          }}
        >
          CLOSE ✕
        </div>
      </div>
    </Sheet>
  );
}
